<script setup lang="ts">
/**
 * A select you can type into.
 *
 * The taxonomy lists this exists for are long and alphabetical — sixty-four
 * conditions, twenty-two carriers, twenty-one specialties — and a native
 * `<select>` over a list that size is a scroll with no way to aim. Typing
 * "trauma" is how a clinician actually knows what they are looking for.
 *
 * **It is not a text field, and the distinction is the whole design.** What the
 * caller gets back is a taxonomy id from `options`, never the prose in the box:
 * the wire format is ids everywhere, and a control that could return a string
 * nobody offered would make the client the authority on a vocabulary the
 * database owns. Typing filters; it does not compose an answer. Anything left
 * in the box that matches nothing is discarded when the list closes, and the
 * box goes back to saying what is actually selected.
 *
 * **The ARIA is the contract, not decoration.** `role="combobox"` with
 * `aria-expanded`, `aria-controls`, `aria-autocomplete` and
 * `aria-activedescendant` is what makes this announce as a combobox rather than
 * as a text field that mysteriously changes the page. The active option moves
 * with the arrow keys *without* moving focus — focus stays in the input, which
 * is why `aria-activedescendant` exists and why the options are not focusable.
 * How many rows the typing left is said in a live region, because a list that
 * shrinks silently is a list a screen-reader user cannot see shrink.
 *
 * **The list opens when asked, not when focused.** It used to open on focus,
 * and in a vertical panel of five of these that meant tabbing past a field
 * dropped its list over the next one, every screen reader announced "expanded"
 * on every Tab, and — the part that was a bug rather than a nuisance — Enter
 * straight after focusing took the first row, which is "Any …" wherever there
 * is an empty label, so it silently cleared the answer. The list now opens on
 * a click, on typing, or on the arrow keys, which is when a reader has said
 * they want it.
 *
 * Keyboard, in full, because a combobox that only works with a mouse is a
 * worse native select:
 *
 *   - **Down/Up** open the list at the current answer and move the active row,
 *     wrapping at both ends. **Home/End** jump to the ends of an open list.
 *   - **Enter** takes the active row, and is swallowed when it does, because
 *     this control lives inside a form whose submit button runs a search and
 *     picking a diagnosis is not searching. With the list closed, or open with
 *     no row active, Enter is the form's: a reader who has finished filling in
 *     the panel and presses Enter in a field expects the search to run.
 *   - **Escape** closes the list and restores the selected label, so an
 *     exploratory search leaves no trace.
 *   - **Tab** commits nothing; whatever was typed is discarded. Moving on is
 *     not choosing.
 *
 * **Clicks inside the list never take focus from the input.** The list's
 * `mousedown` is prevented, so choosing a row, or dragging the list's own
 * scrollbar, happens with the input still focused and the list still open.
 * Blur therefore means the reader left — Tab, or a click somewhere else — and
 * it closes the list.
 */

import { computed, nextTick, ref, useId, useTemplateRef, watch } from 'vue'

/** One row. `id` is what the caller receives; `label` is what a reader reads. */
export interface ComboboxOption {
  readonly id: string
  readonly label: string
}

const props = withDefaults(
  defineProps<{
    /** The input's id, so an `MpField` label can point at it. */
    id?: string | undefined
    /**
     * The accessible name, when nothing else provides one. Omit it inside an
     * `MpField`, which has already labeled the control — two names is one name
     * too many and screen readers announce the wrong one.
     */
    ariaLabel?: string | undefined
    options: readonly ComboboxOption[]
    /** Shown when nothing is selected and nothing has been typed. */
    placeholder?: string | undefined
    /**
     * A row at the top of the list that selects nothing — "Any diagnosis".
     *
     * Deliberately an option rather than a clear button: "any" is a real answer
     * to "which diagnosis", not the absence of one, and putting it in the list
     * means it can be reached by keyboard like every other answer.
     */
    emptyLabel?: string | undefined
    disabled?: boolean | undefined
    /**
     * `MpField`'s slot props, passed straight through to the input.
     *
     * They belong on the control a reader is focused on, not on the wrapper
     * around it: a hint and an error announced from a `div` the input is inside
     * are a hint and an error nobody hears.
     */
    describedBy?: string | undefined
    invalid?: boolean | undefined
    /**
     * For a parent that draws the field itself — the search screen's token box.
     *
     * The input loses its border, background and padding, and the list hangs
     * from the nearest positioned ancestor rather than from this control, so it
     * spans the parent's box. The parent owns the boundary a reader sees as the
     * field, and the anchor with it. The focus ring stays until the parent
     * turns it off with `--mp-combobox-ring: none` and
     * `--mp-combobox-forced-outline: none`, which it does beside the ring it
     * draws instead.
     * A prop rather than `:deep()` overrides, because a parent restyling this
     * component's inner classes has made its markup a contract nobody wrote
     * down, and renaming one class would break the parent silently.
     */
    bare?: boolean | undefined
    /**
     * Whether a row answers what was typed. Substring on the label by default.
     *
     * For a list whose labels are not how people type them: a time field lists
     * "6:00 PM" and a reader types "6p" or "1800". The needle arrives trimmed
     * and lowercased. What a row *is* stays its id, so a matcher can only
     * change which rows are offered, never what the caller receives.
     */
    matcher?: ((option: ComboboxOption, needle: string) => boolean) | undefined
  }>(),
  { placeholder: 'Search', disabled: false, bare: false }
)

/**
 * The selected taxonomy id. Empty string is "nothing selected".
 *
 * `undefined` is accepted and read as empty, because a form library's field ref
 * is `string | undefined` before anything is chosen — `vee-validate`'s
 * `defineField` is one, and a control every form has to wrap in a coercing
 * computed is a control that has pushed its problem onto its callers.
 */
const model = defineModel<string | undefined>({ default: '' })

const selected = computed({
  get: () => model.value ?? '',
  set: (next: string) => {
    model.value = next
  }
})

const listId = useId()
const optionId = (index: number): string => `${listId}-option-${index}`

const root = useTemplateRef<HTMLDivElement>('root')
const input = useTemplateRef<HTMLInputElement>('input')
const list = useTemplateRef<HTMLUListElement>('list')

/** What is in the box. Not the answer — see the note about ids above. */
const typed = ref('')
const open = ref(false)

/** The row Enter would take. `-1` is none, which is how a list opens by click. */
const active = ref(-1)

const selectedLabel = computed(() =>
  props.options.find(option => option.id === selected.value)?.label ?? '')

/**
 * The list, filtered by what has been typed.
 *
 * Case-insensitive substring rather than a prefix match: "trauma" should find
 * `Trauma & PTSD`, and a clinician thinking of eating disorders types
 * "eating", which is where the useful words are in half these labels.
 *
 * The "any" row shows only while nothing is typed. It is not a match for any
 * search term — nobody types "any" looking for it — and it is the way back to no
 * answer, which a reader reaches by clearing the box.
 */
const matches = computed<readonly ComboboxOption[]>(() => {
  const needle = typed.value.trim().toLowerCase()
  const rows = needle === ''
    ? props.options
    : props.options.filter(option => props.matcher !== undefined
      ? props.matcher(option, needle)
      : option.label.toLowerCase().includes(needle))

  return props.emptyLabel !== undefined && needle === ''
    ? [{ id: '', label: props.emptyLabel }, ...rows]
    : rows
})

/** Where the current answer sits in the list, or -1 when it is not there. */
const selectedIndex = computed(() => matches.value.findIndex(option => option.id === selected.value))

/**
 * Said out loud as the list narrows. Empty — and so silent — until something
 * has been typed, because announcing the full count on every open is noise.
 */
const announcement = computed(() => {
  if (!open.value || typed.value.trim() === '') return ''
  const count = matches.value.length
  if (count === 0) return `Nothing matches ${typed.value.trim()}`
  return count === 1 ? '1 result' : `${count} results`
})

/**
 * Whether the text in the box at rest is an answer or a prompt.
 *
 * The placeholder carries the selected label when there is one, and "Any
 * diagnosis" is an answer too; both read at full ink. "Add a need…" is a prompt
 * and reads muted, or a token box would look like it already held an answer.
 */
const valued = computed(() => selectedLabel.value !== '' || props.emptyLabel !== undefined)

/** What the box shows when it is not being typed into. */
function reset (): void {
  typed.value = ''
  active.value = -1
}

// The label follows the model, so a filter cleared from somewhere else on the
// screen — the empty state's actions, Clear all — is reflected here rather than
// leaving stale prose in a box nobody edited.
watch(selected, () => {
  if (!open.value) reset()
})

/** Opens at the current answer, so Enter on an untouched list changes nothing. */
function show (): void {
  if (props.disabled) return
  open.value = true
  active.value = selectedIndex.value
}

function close (): void {
  open.value = false
  reset()
}

function choose (option: ComboboxOption): void {
  selected.value = option.id
  close()
  input.value?.focus()
}

function onInput (event: Event): void {
  typed.value = (event.target as HTMLInputElement).value
  open.value = true
  // Typing is intent, so the best match is ready for Enter. Clearing the box is
  // not a match for anything, so it goes back to the current answer.
  active.value = typed.value.trim() === '' ? selectedIndex.value : 0
}

/** A click opens a closed list; a second click on an untouched one closes it. */
function onInputMousedown (): void {
  if (!open.value) show()
  else if (typed.value === '') close()
}

/**
 * Keeps the active row in view when the arrows walk past the visible rows.
 *
 * Called optionally, because this is a convenience and not the behavior: jsdom
 * has no `scrollIntoView`, and a control whose keyboard navigation throws in a
 * test environment is a control nobody can write a test for.
 */
async function scrollActiveIntoView (): Promise<void> {
  await nextTick()
  list.value?.children[active.value]?.scrollIntoView?.({ block: 'nearest' })
}

function move (delta: number): void {
  if (!open.value) {
    show()
    if (active.value === -1 && matches.value.length > 0) active.value = 0
    void scrollActiveIntoView()
    return
  }

  const count = matches.value.length
  if (count === 0) return

  // Wraps, so Up from the first row reaches the last one without a scroll. From
  // no active row, Down starts at the top and Up at the bottom.
  active.value = active.value === -1
    ? (delta > 0 ? 0 : count - 1)
    : (active.value + delta + count) % count
  void scrollActiveIntoView()
}

function onKeydown (event: KeyboardEvent): void {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      move(1)
      return

    case 'ArrowUp':
      event.preventDefault()
      move(-1)
      return

    case 'Enter': {
      if (!open.value) return
      const option = matches.value[active.value]
      if (option === undefined) {
        // Open with nothing active: the reader has not picked a row, so the
        // Enter belongs to the form. Closing first discards any typed text.
        close()
        return
      }
      event.preventDefault()
      choose(option)
      return
    }

    case 'Escape':
      if (!open.value) return
      event.preventDefault()
      event.stopPropagation()
      close()
      return

    case 'Home':
    case 'End':
      if (!open.value || matches.value.length === 0) return
      event.preventDefault()
      active.value = event.key === 'Home' ? 0 : matches.value.length - 1
      void scrollActiveIntoView()
  }
}

/** Nothing typed is kept. A box reading "traum" over another answer misleads. */
function onBlur (): void {
  close()
}

/**
 * For a parent that draws a bigger box around this one — the search screen's
 * token box, whose padding and chip rows read as part of the field. Exposed so
 * the parent can forward a click without reaching through to the input.
 */
function focus (): void {
  input.value?.focus()
}

/** Focuses and opens, for the same parent, when a click means "show me the list". */
function openList (): void {
  focus()
  if (!open.value) show()
}

/** Whether a node is part of this control, so a parent can tell its own clicks apart. */
function owns (node: Node | null): boolean {
  return node !== null && (root.value?.contains(node) ?? false)
}

defineExpose({ focus, openList, owns })
</script>

<template>
  <div
    ref="root"
    class="mp-combobox"
    :class="{ 'mp-combobox--bare': props.bare }"
  >
    <div class="mp-combobox__box">
      <input
        :id="props.id"
        ref="input"
        class="mp-combobox__input"
        :class="{ 'mp-combobox__input--valued': valued }"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck="false"
        aria-autocomplete="list"
        :aria-label="props.ariaLabel"
        :aria-describedby="props.describedBy"
        :aria-invalid="props.invalid"
        :aria-expanded="open"
        :aria-controls="listId"
        :aria-activedescendant="open && active >= 0 && active < matches.length ? optionId(active) : undefined"
        :disabled="props.disabled"
        :placeholder="selectedLabel === '' ? props.placeholder : selectedLabel"
        :value="open ? typed : selectedLabel"
        @input="onInput"
        @keydown="onKeydown"
        @mousedown="onInputMousedown"
        @blur="onBlur"
      >

      <!-- Not a button. The input is the control, and a second tab stop that
           only opens a list the arrows already open is a stop in everybody's
           way. `aria-hidden` for the same reason the chevron on a select is
           not announced. -->
      <span
        class="mp-combobox__chevron"
        aria-hidden="true"
      />
    </div>

    <!-- `mousedown.prevent` on the whole popup, not only on the rows, so the
         list's scrollbar can be dragged without the input losing focus and the
         list closing under the pointer. -->
    <div
      v-show="open"
      class="mp-combobox__popup"
      @mousedown.prevent
    >
      <ul
        :id="listId"
        ref="list"
        class="mp-combobox__list"
        role="listbox"
      >
        <li
          v-for="(option, index) in matches"
          :id="optionId(index)"
          :key="option.id === '' ? '__any' : option.id"
          class="mp-combobox__option"
          :class="{ 'mp-combobox__option--active': index === active }"
          role="option"
          :aria-selected="option.id === selected"
          @mousedown="choose(option)"
          @mousemove="active = index"
        >
          {{ option.label }}
        </li>
      </ul>

      <!-- Outside the listbox, where a row that is not an option belongs, and
           hidden from assistive technology because the status region below
           already says it. -->
      <p
        v-if="matches.length === 0"
        class="mp-combobox__empty"
        aria-hidden="true"
      >
        Nothing matches “{{ typed }}”
      </p>
    </div>

    <!-- Always mounted, with only its text changing: a live region that
         arrives already filled is one several screen readers never read. -->
    <p
      class="mp-sr-only"
      role="status"
    >
      {{ announcement }}
    </p>
  </div>
</template>

<style scoped>
.mp-combobox {
  position: relative;
}

/* Static, so the popup hangs from the parent's positioned box instead. */
.mp-combobox--bare {
  position: static;
}

.mp-combobox__box {
  position: relative;
  display: flex;
}

/* `MpField`'s control appearance, by hand, for the reason `MpSearchField` gives:
   this owns a composed box. The tokens are the same ones. */
.mp-combobox__input {
  inline-size: 100%;
  min-block-size: var(--target-min);
  padding: var(--space-2) var(--space-3);
  padding-inline-end: var(--space-6);
  background: var(--surface-raised);
  color: var(--text-primary);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-control);
  font-family: var(--font-body);
  font-size: var(--font-size-body);
}

/* The chevron's room, restated at a specificity `MpField` cannot beat.
   `MpField` styles whatever its slot renders through `:deep(input)`, which
   compiles to (0,2,1) and outranks the single class above, so its symmetric
   padding used to win and long labels ran under the chevron. Two classes is
   (0,3,0). Only the padding is restated, so `MpField`'s invalid-state border
   still applies. */
.mp-combobox .mp-combobox__input {
  padding-inline-end: var(--space-6);
}

.mp-combobox--bare .mp-combobox__input {
  min-block-size: auto;
  padding: 0;
  padding-inline-end: var(--space-5);
  border: 0;
  background: transparent;
}

/* The ring stays on, and a parent that draws its own turns this one off.
   A parent drawing the ring on its box — the boundary a reader sees — wants no
   second ring a few pixels inside it, so it sets `--mp-combobox-ring` and
   `--mp-combobox-forced-outline` to `none` in the same rule set as the ring it
   draws. That keeps base.css's promise that an indicator is never deleted by a
   file that does not also supply its replacement: removing it here, by
   default, left any `bare` combobox in a parent that forgot with no focus
   indicator at all. Forgetting now costs a tight ring, not an invisible one. */
.mp-combobox--bare .mp-combobox__input:focus-visible {
  box-shadow: var(--mp-combobox-ring, var(--focus-ring));
}

@media (forced-colors: active) {
  .mp-combobox--bare .mp-combobox__input:focus-visible {
    outline: var(--mp-combobox-forced-outline, 3px solid CanvasText);
  }
}

/* A prompt reads muted; an answer — the selected label, or "Any …" — reads at
   full ink, even though the browser draws both as a placeholder. */
.mp-combobox__input::placeholder {
  color: var(--text-muted);
  opacity: 1;
}

.mp-combobox__input--valued::placeholder {
  color: var(--text-primary);
}

.mp-combobox__input:disabled,
.mp-combobox__input:disabled::placeholder {
  color: var(--text-muted);
}

/* A rotated corner rather than an SVG: it takes a theme token, where a data URI
   would need one copy per theme and would drift from the text beside it. */
.mp-combobox__chevron {
  position: absolute;
  inset-inline-end: var(--space-3);
  inset-block-start: 50%;
  inline-size: 0.45rem;
  block-size: 0.45rem;
  margin-block-start: -0.35rem;
  border-inline-end: 2px solid var(--text-secondary);
  border-block-end: 2px solid var(--text-secondary);
  transform: rotate(45deg);
  pointer-events: none;
}

.mp-combobox--bare .mp-combobox__chevron {
  inset-inline-end: var(--space-1);
}

.mp-combobox__popup {
  position: absolute;
  z-index: 20;
  inset-inline: 0;
  inset-block-start: calc(100% + var(--space-1));
  max-block-size: 16rem;
  overflow-y: auto;
  padding: var(--space-1);
  background: var(--surface-raised);
  border: 1px solid var(--border-control);
  border-radius: var(--radius-control);
  box-shadow: var(--shadow-overlay);
}

.mp-combobox__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Rows at the touch floor and at body size. DESIGN.md section 8 sets 44px for
   anything a thumb has to hit, and a row in small type under a value in body
   type reads as a footnote to the answer it offers. */
.mp-combobox__option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-block-size: var(--target-min);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-control);
  color: var(--text-primary);
  font-size: var(--font-size-body);
  cursor: pointer;
}

/* The active row is marked by fill and by weight rather than by color alone.
   DESIGN.md section 8. */
.mp-combobox__option--active {
  background: color-mix(in srgb, var(--text-primary) 10%, transparent);
  font-weight: 600;
}

/* `/ ''` is the alternative text: the mark is drawn and not announced, because
   `aria-selected` has already said it and "check mark, selected" says it twice. */
.mp-combobox__option[aria-selected='true']::after {
  content: '✓' / '';
  margin-inline-start: auto;
  color: var(--text-secondary);
}

.mp-combobox__empty {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  color: var(--text-secondary);
  font-size: var(--font-size-body);
}

/* Forced-colors mode replaces the fill with Canvas, which would leave the active
   row marked by weight alone — too faint to follow down the list. */
@media (forced-colors: active) {
  .mp-combobox__option--active {
    outline: 2px solid Highlight;
    outline-offset: -2px;
  }
}
</style>
